import type { PulsePoint } from "@shared/ui/landing/world-pulse-map"

export type ThreatSeverity = "critical" | "high" | "medium" | "low"

export type ThreatEvent = {
  id: string
  severity: ThreatSeverity
  titleKey: string
  regionKey: string
  chainKey: string
  timestamp: string
}

export type DetectionCapability = {
  id: string
  icon: "shield" | "radar" | "lock" | "eye" | "zap" | "globe"
  titleKey: string
  descriptionKey: string
}

export type KnowledgeArticle = {
  id: string
  categoryKey: string
  titleKey: string
  excerptKey: string
  readMinutes: number
}

export type VoiceReview = {
  id: string
  nameKey: string
  roleKey: string
  quoteKey: string
  rating: number
}

export type ReportMetric = {
  id: string
  labelKey: string
  value: number
  suffix?: string
  trend: "up" | "down" | "stable"
}

export const SECURITY_SCORE = 95
export const SECURITY_SCORE_MAX = 100

export const scoreBreakdown = [
  { id: "wallet", labelKey: "walletIntegrity", score: 98 },
  { id: "contract", labelKey: "contractSafety", score: 96 },
  { id: "network", labelKey: "networkHealth", score: 94 },
  { id: "compliance", labelKey: "complianceStatus", score: 92 },
] as const

export const detectionCapabilities: DetectionCapability[] = [
  {
    id: "phishing",
    icon: "shield",
    titleKey: "phishingTitle",
    descriptionKey: "phishingDesc",
  },
  {
    id: "malware",
    icon: "radar",
    titleKey: "malwareTitle",
    descriptionKey: "malwareDesc",
  },
  {
    id: "approval",
    icon: "lock",
    titleKey: "approvalTitle",
    descriptionKey: "approvalDesc",
  },
  {
    id: "monitoring",
    icon: "eye",
    titleKey: "monitoringTitle",
    descriptionKey: "monitoringDesc",
  },
  {
    id: "flash",
    icon: "zap",
    titleKey: "flashTitle",
    descriptionKey: "flashDesc",
  },
  {
    id: "poison",
    icon: "globe",
    titleKey: "poisonTitle",
    descriptionKey: "poisonDesc",
  },
]

export const threatEvents: ThreatEvent[] = [
  {
    id: "t1",
    severity: "critical",
    titleKey: "eventDrainer",
    regionKey: "regionApac",
    chainKey: "chainTron",
    timestamp: "2026-06-20T08:12:00Z",
  },
  {
    id: "t2",
    severity: "high",
    titleKey: "eventPhishing",
    regionKey: "regionEu",
    chainKey: "chainTron",
    timestamp: "2026-06-20T07:45:00Z",
  },
  {
    id: "t3",
    severity: "medium",
    titleKey: "eventApproval",
    regionKey: "regionNa",
    chainKey: "chainTron",
    timestamp: "2026-06-20T07:30:00Z",
  },
  {
    id: "t4",
    severity: "high",
    titleKey: "eventEnergy",
    regionKey: "regionMe",
    chainKey: "chainTron",
    timestamp: "2026-06-20T07:15:00Z",
  },
  {
    id: "t5",
    severity: "low",
    titleKey: "eventDust",
    regionKey: "regionSa",
    chainKey: "chainTron",
    timestamp: "2026-06-20T06:58:00Z",
  },
  {
    id: "t6",
    severity: "critical",
    titleKey: "eventFakeUsdt",
    regionKey: "regionApac",
    chainKey: "chainTron",
    timestamp: "2026-06-20T06:40:00Z",
  },
]

export const reportMetrics: ReportMetric[] = [
  { id: "threats", labelKey: "threatsBlocked", value: 12847, trend: "up" },
  { id: "wallets", labelKey: "walletsProtected", value: 94200, suffix: "+", trend: "up" },
  { id: "response", labelKey: "avgResponse", value: 1.2, suffix: "s", trend: "down" },
  { id: "accuracy", labelKey: "detectionAccuracy", value: 99.7, suffix: "%", trend: "stable" },
]

export const reportFindings = [
  { id: "f1", severity: "critical" as const, titleKey: "findingDrainer" },
  { id: "f2", severity: "high" as const, titleKey: "findingPhishing" },
  { id: "f3", severity: "medium" as const, titleKey: "findingApproval" },
  { id: "f4", severity: "low" as const, titleKey: "findingDust" },
]

export const pulsePoints: PulsePoint[] = [
  { name: "Singapore", coordinates: [103.8198, 1.3521] },
  { name: "Frankfurt", coordinates: [8.6821, 50.1109] },
  { name: "Virginia", coordinates: [-77.436, 37.5407] },
  { name: "Tokyo", coordinates: [139.6917, 35.6895] },
  { name: "São Paulo", coordinates: [-46.6333, -23.5505] },
  { name: "Dubai", coordinates: [55.2708, 25.2048] },
  { name: "Seoul", coordinates: [126.978, 37.5665] },
  { name: "London", coordinates: [-0.1276, 51.5074] },
]

export const networkStats = [
  { id: "nodes", labelKey: "activeNodes", value: 847 },
  { id: "regions", labelKey: "regionsCovered", value: 42 },
  { id: "latency", labelKey: "avgLatency", value: 38, suffix: "ms" },
]

export const knowledgeArticles: KnowledgeArticle[] = [
  {
    id: "k1",
    categoryKey: "catSecurity",
    titleKey: "article1Title",
    excerptKey: "article1Excerpt",
    readMinutes: 5,
  },
  {
    id: "k2",
    categoryKey: "catUsdt",
    titleKey: "article2Title",
    excerptKey: "article2Excerpt",
    readMinutes: 7,
  },
  {
    id: "k3",
    categoryKey: "catWallet",
    titleKey: "article3Title",
    excerptKey: "article3Excerpt",
    readMinutes: 4,
  },
  {
    id: "k4",
    categoryKey: "catCompliance",
    titleKey: "article4Title",
    excerptKey: "article4Excerpt",
    readMinutes: 6,
  },
]

export const voiceReviews: VoiceReview[] = [
  {
    id: "v1",
    nameKey: "reviewer1Name",
    roleKey: "reviewer1Role",
    quoteKey: "reviewer1Quote",
    rating: 5,
  },
  {
    id: "v2",
    nameKey: "reviewer2Name",
    roleKey: "reviewer2Role",
    quoteKey: "reviewer2Quote",
    rating: 5,
  },
  {
    id: "v3",
    nameKey: "reviewer3Name",
    roleKey: "reviewer3Role",
    quoteKey: "reviewer3Quote",
    rating: 4,
  },
]

export function formatThreatTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso))
}

export function severityVariant(
  severity: ThreatSeverity
): "danger" | "warning" | "info" | "neutral" {
  switch (severity) {
    case "critical":
      return "danger"
    case "high":
      return "warning"
    case "medium":
      return "info"
    default:
      return "neutral"
  }
}

function hashString(input: string): number {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function seededRandom(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

export type ScanStepId = "scanning" | "sanctions" | "analyzing" | "calculating"

export type ScanResult = {
  address: string
  riskScore: number
  riskLevel: "low" | "medium" | "high" | "critical"
  flags: string[]
  lastSeen: string
  volumeUsd: number
}

export function resolveScanResult(address: string): ScanResult {
  const normalized = address.trim()
  const rand = seededRandom(hashString(normalized.toLowerCase()))
  const riskScore = Math.round(18 + rand() * 74)
  const riskLevel =
    riskScore >= 80
      ? "critical"
      : riskScore >= 60
        ? "high"
        : riskScore >= 35
          ? "medium"
          : "low"

  const flagPool = [
    "sanctions_proximity",
    "mixer_exposure",
    "high_velocity",
    "darknet_cluster",
    "jurisdiction_alert",
    "scam_pattern",
  ] as const

  const flagCount = 2 + Math.floor(rand() * 3)
  const flags: string[] = []
  for (let i = 0; i < flagCount; i += 1) {
    const flag = flagPool[Math.floor(rand() * flagPool.length)] ?? flagPool[0]
    if (!flags.includes(flag)) flags.push(flag)
  }

  const hoursAgo = Math.floor(rand() * 72)
  const lastSeen = new Date(Date.now() - hoursAgo * 3_600_000).toISOString()
  const volumeUsd = Number((rand() * 4_500_000 + 12_000).toFixed(0))

  return {
    address: normalized,
    riskScore,
    riskLevel,
    flags,
    lastSeen,
    volumeUsd,
  }
}
